<section class="workspace" [class.workspace--dark]="darkMode()">
  <div class="workspace__shell">
    <app-navbar
      [appName]="'app.name' | translate"
      [moduleName]="'advanced.moduleName' | translate"
      [moduleBadge]="'navbar.module3' | translate"
      logoutLink="/"
      [darkMode]="darkMode()"
      [language]="translationService.language()"
      [languages]="languages"
      (themeToggle)="toggleTheme()"
      (languageChange)="setLanguage($event)" />

    <main class="workspace__content">
      <section class="workspace__hero">
        <div class="workspace__hero-copy">
          <p class="workspace__eyebrow">{{ 'advanced.eyebrow' | translate }}</p>
          <h1>{{ 'advanced.title' | translate }}</h1>
          <p class="workspace__copy">
            {{ 'advanced.copy' | translate }}
          </p>
        </div>
      </section>

      <nav class="tab-strip" aria-label="Valuation type">
        @for (tab of tabs; track tab) {
          <button type="button" class="tab-strip__item" [class.tab-strip__item--active]="activeTab === tab"
            (click)="selectTab(tab)">
            {{ ('advanced.tabs.' + tab) | translate }}
          </button>
        }
      </nav>

      <section class="workspace__grid workspace__grid--single">
        <article class="panel panel--form">
          <div class="panel__toolbar">
            <span class="panel__tab panel__tab--active">{{ ('advanced.tabs.' + activeTab) | translate }}</span>
          </div>

          @switch (activeTab) {
            @case ('landTransaction') {
              <form class="table-form" (ngSubmit)="submitLandTransactions()" aria-label="Land transaction form">
                @for (record of landTransactions.controls; track $index; let i = $index) {
                  <fieldset class="table-form__group" [formGroup]="record">
                    <legend class="table-form__group-title">{{ 'advanced.form.recordLabel' | translate }} {{ i + 1 }}</legend>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'land-cadastral-area-' + i">{{ 'advanced.form.cadastralAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'land-cadastral-area-' + i" type="text" formControlName="cadastralArea"
                          [placeholder]="'advanced.form.cadastralAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.cadastralArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.cadastralArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row table-form__row--stacked">
                      <label class="table-form__label">{{ 'advanced.form.parcelNumbersLabel' | translate }}</label>
                      <div class="table-form__value">
                        <div class="array-field">
                          @for (control of parcelNumbersOf(i).controls; track $index; let pIndex = $index) {
                            <div class="array-field__row">
                              <input type="text" [formControl]="control"
                                [placeholder]="'advanced.form.parcelNumbersPlaceholder' | translate"
                                [class.table-form__input--invalid]="parcelNumberHasError(i, pIndex)" />
                              <button type="button" class="array-field__remove" (click)="removeParcelNumber(i, pIndex)">
                                {{ 'advanced.form.removeItem' | translate }}
                              </button>
                            </div>
                          }
                          <button type="button" class="array-field__add" (click)="addParcelNumber(i)">
                            {{ 'advanced.form.addParcelNumber' | translate }}
                          </button>
                        </div>
                      </div>
                    </div>
                    @for (control of parcelNumbersOf(i).controls; track $index; let pIndex = $index) {
                      @if (parcelNumberHasError(i, pIndex)) {
                        <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                      }
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'land-total-area-' + i">{{ 'advanced.form.totalAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'land-total-area-' + i" type="number" formControlName="totalArea"
                          [placeholder]="'advanced.form.totalAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.totalArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.totalArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'land-realized-price-' + i">{{ 'advanced.form.realizedPriceLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'land-realized-price-' + i" type="number" formControlName="realizedPrice"
                          [placeholder]="'advanced.form.realizedPricePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.realizedPrice)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.realizedPrice)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'land-price-per-m2-' + i">{{ 'advanced.form.pricePerM2Label' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'land-price-per-m2-' + i" type="number" formControlName="pricePerM2"
                          [placeholder]="'advanced.form.pricePerM2Placeholder' | translate" />
                      </div>
                    </div>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'land-date-' + i">{{ 'advanced.form.dateLabel' | translate }}</label>
                      <div class="table-form__value">
                        <app-date-picker [inputId]="'land-date-' + i" formControlName="date" [compact]="true"
                          [placeholder]="'advanced.form.datePlaceholder' | translate"
                          [invalid]="hasError(record.controls.date)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.date)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'land-case-number-' + i">{{ 'advanced.form.caseNumberLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'land-case-number-' + i" type="text" formControlName="caseNumber"
                          [placeholder]="'advanced.form.caseNumberPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.caseNumber)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.caseNumber)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'land-source-' + i">{{ 'advanced.form.sourceLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'land-source-' + i" type="text" formControlName="source"
                          [placeholder]="'advanced.form.sourcePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.source)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.source)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'land-land-use-category-' + i">{{ 'advanced.form.landUseCategoryLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'land-land-use-category-' + i" type="text" formControlName="landUseCategory"
                          [placeholder]="'advanced.form.landUseCategoryPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.landUseCategory)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.landUseCategory)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'land-built-status-' + i">{{ 'advanced.form.builtStatusLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'land-built-status-' + i" type="text" formControlName="builtStatus"
                          [placeholder]="'advanced.form.builtStatusPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.builtStatus)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.builtStatus)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row table-form__row--stacked">
                      <span class="table-form__label">{{ 'advanced.form.imagesLabel' | translate }}</span>
                      <div class="table-form__value">
                        <div class="array-field">
                          @if (imagesOf(landTransactions, i).length < 3) {
                            <app-file-dropzone accept="image/*" [multiple]="true"
                              [title]="'advanced.form.imagesDropTitle' | translate"
                              [hint]="'advanced.form.imagesDropHint' | translate"
                              (filesSelected)="onImagesSelected($event, landTransactions, i)" />
                          }
                          <p class="table-form__hint">{{ 'advanced.form.imagesLimitHint' | translate }}</p>
                          @if (imagesOf(landTransactions, i).length) {
                            <div class="file-list">
                              @for (control of imagesOf(landTransactions, i).controls; track $index; let iIndex = $index) {
                                <div class="file-list__item">
                                  <span class="file-list__icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" focusable="false">
                                      <path fill="currentColor" d="M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm1 14h12l-3.75-5-2.75 3.5-2-2.5L6 18Zm2.5-8a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
                                    </svg>
                                  </span>
                                  <span class="file-list__meta">
                                    <strong>{{ imageFileName(landTransactions, i, iIndex) }}</strong>
                                    <small>{{ imageFileSize(landTransactions, i, iIndex) }}</small>
                                  </span>
                                  <button type="button" class="file-list__remove" (click)="removeImage(landTransactions, i, iIndex)"
                                    [attr.aria-label]="'Remove ' + imageFileName(landTransactions, i, iIndex)">
                                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                      <path fill="currentColor" d="m18.3 5.71-1.41-1.41L12 9.17 7.11 4.29 5.7 5.71 10.59 10.6 5.7 15.49l1.41 1.41L12 12.01l4.89 4.89 1.41-1.41-4.89-4.89Z" />
                                    </svg>
                                  </button>
                                </div>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    </div>

                    <div class="table-form__actions">
                      <button type="button" class="array-field__remove" (click)="removeRecord(landTransactions, i)">
                        {{ 'advanced.form.removeRecord' | translate }}
                      </button>
                    </div>
                  </fieldset>
                }

                <div class="table-form__actions">
                  <button type="button" class="array-field__add" (click)="addLandTransaction()">
                    {{ 'advanced.form.addRecord' | translate }}
                  </button>
                </div>

                <div class="table-form__actions">
                  <button type="button" class="table-form__clear" (click)="resetLandTransactions()" [disabled]="isSubmitting">
                    {{ 'advanced.form.clearForm' | translate }}
                  </button>
                  <button type="submit" class="table-form__submit" [disabled]="isSubmitting">
                    {{ isSubmitting ? ('advanced.form.submitting' | translate) : ('advanced.form.submit' | translate) }}
                  </button>
                </div>

                @if (errorMessage) {
                  <p class="form-status form-status--error">{{ errorMessage }}</p>
                }
                @if (successMessage) {
                  <p class="form-status form-status--success">{{ successMessage }}</p>
                }
              </form>
            }

            @case ('agriculturalLand') {
              <form class="table-form" (ngSubmit)="submitAgriculturalLand()" aria-label="Agricultural land form">
                @for (record of agriculturalLands.controls; track $index; let i = $index) {
                  <fieldset class="table-form__group" [formGroup]="record">
                    <legend class="table-form__group-title">{{ 'advanced.form.recordLabel' | translate }} {{ i + 1 }}</legend>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'agri-cadastral-area-' + i">{{ 'advanced.form.cadastralAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'agri-cadastral-area-' + i" type="text" formControlName="cadastralArea"
                          [placeholder]="'advanced.form.cadastralAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.cadastralArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.cadastralArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'agri-parcel-number-' + i">{{ 'advanced.form.parcelNumberLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'agri-parcel-number-' + i" type="text" formControlName="parcelNumber"
                          [placeholder]="'advanced.form.parcelNumberPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.parcelNumber)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.parcelNumber)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'agri-land-type-' + i">{{ 'advanced.form.landTypeLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'agri-land-type-' + i" type="text" formControlName="landType"
                          [placeholder]="'advanced.form.landTypePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.landType)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.landType)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'agri-total-area-' + i">{{ 'advanced.form.totalAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'agri-total-area-' + i" type="number" formControlName="totalArea"
                          [placeholder]="'advanced.form.totalAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.totalArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.totalArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'agri-realized-price-' + i">{{ 'advanced.form.realizedPriceLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'agri-realized-price-' + i" type="number" formControlName="realizedPrice"
                          [placeholder]="'advanced.form.realizedPricePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.realizedPrice)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.realizedPrice)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'agri-price-per-m2-' + i">{{ 'advanced.form.pricePerM2Label' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'agri-price-per-m2-' + i" type="number" formControlName="pricePerM2"
                          [placeholder]="'advanced.form.pricePerM2Placeholder' | translate" />
                      </div>
                    </div>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'agri-date-' + i">{{ 'advanced.form.dateLabel' | translate }}</label>
                      <div class="table-form__value">
                        <app-date-picker [inputId]="'agri-date-' + i" formControlName="date" [compact]="true"
                          [placeholder]="'advanced.form.datePlaceholder' | translate"
                          [invalid]="hasError(record.controls.date)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.date)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'agri-case-number-' + i">{{ 'advanced.form.caseNumberLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'agri-case-number-' + i" type="text" formControlName="caseNumber"
                          [placeholder]="'advanced.form.caseNumberPlaceholder' | translate" />
                      </div>
                    </div>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'agri-source-' + i">{{ 'advanced.form.sourceLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'agri-source-' + i" type="text" formControlName="source"
                          [placeholder]="'advanced.form.sourcePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.source)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.source)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row table-form__row--stacked">
                      <span class="table-form__label">{{ 'advanced.form.imagesLabel' | translate }}</span>
                      <div class="table-form__value">
                        <div class="array-field">
                          @if (imagesOf(agriculturalLands, i).length < 3) {
                            <app-file-dropzone accept="image/*" [multiple]="true"
                              [title]="'advanced.form.imagesDropTitle' | translate"
                              [hint]="'advanced.form.imagesDropHint' | translate"
                              (filesSelected)="onImagesSelected($event, agriculturalLands, i)" />
                          }
                          <p class="table-form__hint">{{ 'advanced.form.imagesLimitHint' | translate }}</p>
                          @if (imagesOf(agriculturalLands, i).length) {
                            <div class="file-list">
                              @for (control of imagesOf(agriculturalLands, i).controls; track $index; let iIndex = $index) {
                                <div class="file-list__item">
                                  <span class="file-list__icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" focusable="false">
                                      <path fill="currentColor" d="M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm1 14h12l-3.75-5-2.75 3.5-2-2.5L6 18Zm2.5-8a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
                                    </svg>
                                  </span>
                                  <span class="file-list__meta">
                                    <strong>{{ imageFileName(agriculturalLands, i, iIndex) }}</strong>
                                    <small>{{ imageFileSize(agriculturalLands, i, iIndex) }}</small>
                                  </span>
                                  <button type="button" class="file-list__remove" (click)="removeImage(agriculturalLands, i, iIndex)"
                                    [attr.aria-label]="'Remove ' + imageFileName(agriculturalLands, i, iIndex)">
                                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                      <path fill="currentColor" d="m18.3 5.71-1.41-1.41L12 9.17 7.11 4.29 5.7 5.71 10.59 10.6 5.7 15.49l1.41 1.41L12 12.01l4.89 4.89 1.41-1.41-4.89-4.89Z" />
                                    </svg>
                                  </button>
                                </div>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    </div>

                    <div class="table-form__actions">
                      <button type="button" class="array-field__remove" (click)="removeRecord(agriculturalLands, i)">
                        {{ 'advanced.form.removeRecord' | translate }}
                      </button>
                    </div>
                  </fieldset>
                }

                <div class="table-form__actions">
                  <button type="button" class="array-field__add" (click)="addAgriculturalLand()">
                    {{ 'advanced.form.addRecord' | translate }}
                  </button>
                </div>

                <div class="table-form__actions">
                  <button type="button" class="table-form__clear" (click)="resetAgriculturalLand()" [disabled]="isSubmitting">
                    {{ 'advanced.form.clearForm' | translate }}
                  </button>
                  <button type="submit" class="table-form__submit" [disabled]="isSubmitting">
                    {{ isSubmitting ? ('advanced.form.submitting' | translate) : ('advanced.form.submit' | translate) }}
                  </button>
                </div>

                @if (errorMessage) {
                  <p class="form-status form-status--error">{{ errorMessage }}</p>
                }
                @if (successMessage) {
                  <p class="form-status form-status--success">{{ successMessage }}</p>
                }
              </form>
            }

            @case ('apartmentUnit') {
              <form class="table-form" (ngSubmit)="submitApartmentUnits()" aria-label="Apartment unit form">
                @for (record of apartmentUnits.controls; track $index; let i = $index) {
                  <fieldset class="table-form__group" [formGroup]="record">
                    <legend class="table-form__group-title">{{ 'advanced.form.recordLabel' | translate }} {{ i + 1 }}</legend>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'apt-cadastral-area-' + i">{{ 'advanced.form.cadastralAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'apt-cadastral-area-' + i" type="text" formControlName="cadastralArea"
                          [placeholder]="'advanced.form.cadastralAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.cadastralArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.cadastralArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'apt-unit-number-' + i">{{ 'advanced.form.unitNumberLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'apt-unit-number-' + i" type="text" formControlName="unitNumber"
                          [placeholder]="'advanced.form.unitNumberPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.unitNumber)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.unitNumber)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'apt-floor-area-' + i">{{ 'advanced.form.floorAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'apt-floor-area-' + i" type="number" formControlName="floorArea"
                          [placeholder]="'advanced.form.floorAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.floorArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.floorArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'apt-realized-price-' + i">{{ 'advanced.form.realizedPriceLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'apt-realized-price-' + i" type="number" formControlName="realizedPrice"
                          [placeholder]="'advanced.form.realizedPricePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.realizedPrice)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.realizedPrice)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'apt-price-per-m2-' + i">{{ 'advanced.form.pricePerM2Label' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'apt-price-per-m2-' + i" type="number" formControlName="pricePerM2"
                          [placeholder]="'advanced.form.pricePerM2Placeholder' | translate" />
                      </div>
                    </div>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'apt-date-' + i">{{ 'advanced.form.dateLabel' | translate }}</label>
                      <div class="table-form__value">
                        <app-date-picker [inputId]="'apt-date-' + i" formControlName="date" [compact]="true"
                          [placeholder]="'advanced.form.datePlaceholder' | translate"
                          [invalid]="hasError(record.controls.date)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.date)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'apt-case-number-' + i">{{ 'advanced.form.caseNumberLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'apt-case-number-' + i" type="text" formControlName="caseNumber"
                          [placeholder]="'advanced.form.caseNumberPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.caseNumber)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.caseNumber)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'apt-source-' + i">{{ 'advanced.form.sourceLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'apt-source-' + i" type="text" formControlName="source"
                          [placeholder]="'advanced.form.sourcePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.source)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.source)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row table-form__row--stacked">
                      <span class="table-form__label">{{ 'advanced.form.imagesLabel' | translate }}</span>
                      <div class="table-form__value">
                        <div class="array-field">
                          @if (imagesOf(apartmentUnits, i).length < 3) {
                            <app-file-dropzone accept="image/*" [multiple]="true"
                              [title]="'advanced.form.imagesDropTitle' | translate"
                              [hint]="'advanced.form.imagesDropHint' | translate"
                              (filesSelected)="onImagesSelected($event, apartmentUnits, i)" />
                          }
                          <p class="table-form__hint">{{ 'advanced.form.imagesLimitHint' | translate }}</p>
                          @if (imagesOf(apartmentUnits, i).length) {
                            <div class="file-list">
                              @for (control of imagesOf(apartmentUnits, i).controls; track $index; let iIndex = $index) {
                                <div class="file-list__item">
                                  <span class="file-list__icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" focusable="false">
                                      <path fill="currentColor" d="M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm1 14h12l-3.75-5-2.75 3.5-2-2.5L6 18Zm2.5-8a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
                                    </svg>
                                  </span>
                                  <span class="file-list__meta">
                                    <strong>{{ imageFileName(apartmentUnits, i, iIndex) }}</strong>
                                    <small>{{ imageFileSize(apartmentUnits, i, iIndex) }}</small>
                                  </span>
                                  <button type="button" class="file-list__remove" (click)="removeImage(apartmentUnits, i, iIndex)"
                                    [attr.aria-label]="'Remove ' + imageFileName(apartmentUnits, i, iIndex)">
                                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                      <path fill="currentColor" d="m18.3 5.71-1.41-1.41L12 9.17 7.11 4.29 5.7 5.71 10.59 10.6 5.7 15.49l1.41 1.41L12 12.01l4.89 4.89 1.41-1.41-4.89-4.89Z" />
                                    </svg>
                                  </button>
                                </div>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    </div>

                    <div class="table-form__actions">
                      <button type="button" class="array-field__remove" (click)="removeRecord(apartmentUnits, i)">
                        {{ 'advanced.form.removeRecord' | translate }}
                      </button>
                    </div>
                  </fieldset>
                }

                <div class="table-form__actions">
                  <button type="button" class="array-field__add" (click)="addApartmentUnit()">
                    {{ 'advanced.form.addRecord' | translate }}
                  </button>
                </div>

                <div class="table-form__actions">
                  <button type="button" class="table-form__clear" (click)="resetApartmentUnits()" [disabled]="isSubmitting">
                    {{ 'advanced.form.clearForm' | translate }}
                  </button>
                  <button type="submit" class="table-form__submit" [disabled]="isSubmitting">
                    {{ isSubmitting ? ('advanced.form.submitting' | translate) : ('advanced.form.submit' | translate) }}
                  </button>
                </div>

                @if (errorMessage) {
                  <p class="form-status form-status--error">{{ errorMessage }}</p>
                }
                @if (successMessage) {
                  <p class="form-status form-status--success">{{ successMessage }}</p>
                }
              </form>
            }

            @case ('familyHouse') {
              <form class="table-form" (ngSubmit)="submitFamilyHouses()" aria-label="Family house form">
                @for (record of familyHouses.controls; track $index; let i = $index) {
                  <fieldset class="table-form__group" [formGroup]="record">
                    <legend class="table-form__group-title">{{ 'advanced.form.recordLabel' | translate }} {{ i + 1 }}</legend>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-cadastral-area-' + i">{{ 'advanced.form.cadastralAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'house-cadastral-area-' + i" type="text" formControlName="cadastralArea"
                          [placeholder]="'advanced.form.cadastralAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.cadastralArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.cadastralArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-parcel-number-' + i">{{ 'advanced.form.parcelNumberLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'house-parcel-number-' + i" type="text" formControlName="parcelNumber"
                          [placeholder]="'advanced.form.parcelNumberPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.parcelNumber)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.parcelNumber)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-land-area-' + i">{{ 'advanced.form.landAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'house-land-area-' + i" type="number" formControlName="landArea"
                          [placeholder]="'advanced.form.landAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.landArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.landArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-built-area-' + i">{{ 'advanced.form.builtAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'house-built-area-' + i" type="number" formControlName="builtArea"
                          [placeholder]="'advanced.form.builtAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.builtArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.builtArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-usable-area-' + i">{{ 'advanced.form.usableAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'house-usable-area-' + i" type="number" formControlName="usableArea"
                          [placeholder]="'advanced.form.usableAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.usableArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.usableArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-realized-price-' + i">{{ 'advanced.form.realizedPriceLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'house-realized-price-' + i" type="number" formControlName="realizedPrice"
                          [placeholder]="'advanced.form.realizedPricePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.realizedPrice)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.realizedPrice)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-price-per-m2-' + i">{{ 'advanced.form.pricePerM2Label' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'house-price-per-m2-' + i" type="number" formControlName="pricePerM2"
                          [placeholder]="'advanced.form.pricePerM2Placeholder' | translate" />
                      </div>
                    </div>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-date-' + i">{{ 'advanced.form.dateLabel' | translate }}</label>
                      <div class="table-form__value">
                        <app-date-picker [inputId]="'house-date-' + i" formControlName="date" [compact]="true"
                          [placeholder]="'advanced.form.datePlaceholder' | translate"
                          [invalid]="hasError(record.controls.date)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.date)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-case-number-' + i">{{ 'advanced.form.caseNumberLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'house-case-number-' + i" type="text" formControlName="caseNumber"
                          [placeholder]="'advanced.form.caseNumberPlaceholder' | translate" />
                      </div>
                    </div>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'house-source-' + i">{{ 'advanced.form.sourceLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'house-source-' + i" type="text" formControlName="source"
                          [placeholder]="'advanced.form.sourcePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.source)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.source)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row table-form__row--stacked">
                      <span class="table-form__label">{{ 'advanced.form.imagesLabel' | translate }}</span>
                      <div class="table-form__value">
                        <div class="array-field">
                          @if (imagesOf(familyHouses, i).length < 3) {
                            <app-file-dropzone accept="image/*" [multiple]="true"
                              [title]="'advanced.form.imagesDropTitle' | translate"
                              [hint]="'advanced.form.imagesDropHint' | translate"
                              (filesSelected)="onImagesSelected($event, familyHouses, i)" />
                          }
                          <p class="table-form__hint">{{ 'advanced.form.imagesLimitHint' | translate }}</p>
                          @if (imagesOf(familyHouses, i).length) {
                            <div class="file-list">
                              @for (control of imagesOf(familyHouses, i).controls; track $index; let iIndex = $index) {
                                <div class="file-list__item">
                                  <span class="file-list__icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" focusable="false">
                                      <path fill="currentColor" d="M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm1 14h12l-3.75-5-2.75 3.5-2-2.5L6 18Zm2.5-8a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
                                    </svg>
                                  </span>
                                  <span class="file-list__meta">
                                    <strong>{{ imageFileName(familyHouses, i, iIndex) }}</strong>
                                    <small>{{ imageFileSize(familyHouses, i, iIndex) }}</small>
                                  </span>
                                  <button type="button" class="file-list__remove" (click)="removeImage(familyHouses, i, iIndex)"
                                    [attr.aria-label]="'Remove ' + imageFileName(familyHouses, i, iIndex)">
                                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                      <path fill="currentColor" d="m18.3 5.71-1.41-1.41L12 9.17 7.11 4.29 5.7 5.71 10.59 10.6 5.7 15.49l1.41 1.41L12 12.01l4.89 4.89 1.41-1.41-4.89-4.89Z" />
                                    </svg>
                                  </button>
                                </div>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    </div>

                    <div class="table-form__actions">
                      <button type="button" class="array-field__remove" (click)="removeRecord(familyHouses, i)">
                        {{ 'advanced.form.removeRecord' | translate }}
                      </button>
                    </div>
                  </fieldset>
                }

                <div class="table-form__actions">
                  <button type="button" class="array-field__add" (click)="addFamilyHouse()">
                    {{ 'advanced.form.addRecord' | translate }}
                  </button>
                </div>

                <div class="table-form__actions">
                  <button type="button" class="table-form__clear" (click)="resetFamilyHouses()" [disabled]="isSubmitting">
                    {{ 'advanced.form.clearForm' | translate }}
                  </button>
                  <button type="submit" class="table-form__submit" [disabled]="isSubmitting">
                    {{ isSubmitting ? ('advanced.form.submitting' | translate) : ('advanced.form.submit' | translate) }}
                  </button>
                </div>

                @if (errorMessage) {
                  <p class="form-status form-status--error">{{ errorMessage }}</p>
                }
                @if (successMessage) {
                  <p class="form-status form-status--success">{{ successMessage }}</p>
                }
              </form>
            }

            @case ('offerListing') {
              <form class="table-form" (ngSubmit)="submitOfferListings()" aria-label="Offer listing form">
                @for (record of offerListings.controls; track $index; let i = $index) {
                  <fieldset class="table-form__group" [formGroup]="record">
                    <legend class="table-form__group-title">{{ 'advanced.form.recordLabel' | translate }} {{ i + 1 }}</legend>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'offer-property-type-' + i">{{ 'advanced.form.propertyTypeLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'offer-property-type-' + i" type="text" formControlName="propertyType"
                          [placeholder]="'advanced.form.propertyTypePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.propertyType)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.propertyType)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'offer-location-' + i">{{ 'advanced.form.locationLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'offer-location-' + i" type="text" formControlName="location"
                          [placeholder]="'advanced.form.locationPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.location)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.location)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'offer-land-area-' + i">{{ 'advanced.form.landAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'offer-land-area-' + i" type="number" formControlName="landArea"
                          [placeholder]="'advanced.form.landAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.landArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.landArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'offer-usable-area-' + i">{{ 'advanced.form.usableAreaLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'offer-usable-area-' + i" type="number" formControlName="usableArea"
                          [placeholder]="'advanced.form.usableAreaPlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.usableArea)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.usableArea)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'offer-asking-price-' + i">{{ 'advanced.form.askingPriceLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'offer-asking-price-' + i" type="number" formControlName="askingPrice"
                          [placeholder]="'advanced.form.askingPricePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.askingPrice)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.askingPrice)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'offer-price-per-m2-' + i">{{ 'advanced.form.pricePerM2Label' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'offer-price-per-m2-' + i" type="number" formControlName="pricePerM2"
                          [placeholder]="'advanced.form.pricePerM2Placeholder' | translate" />
                      </div>
                    </div>

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'offer-date-' + i">{{ 'advanced.form.dateLabel' | translate }}</label>
                      <div class="table-form__value">
                        <app-date-picker [inputId]="'offer-date-' + i" formControlName="date" [compact]="true"
                          [placeholder]="'advanced.form.datePlaceholder' | translate"
                          [invalid]="hasError(record.controls.date)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.date)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row">
                      <label class="table-form__label" [for]="'offer-website-' + i">{{ 'advanced.form.websiteLabel' | translate }}</label>
                      <div class="table-form__value">
                        <input [id]="'offer-website-' + i" type="text" formControlName="website"
                          [placeholder]="'advanced.form.websitePlaceholder' | translate"
                          [class.table-form__input--invalid]="hasError(record.controls.website)" />
                      </div>
                    </div>
                    @if (hasError(record.controls.website)) {
                      <small class="table-form__error">{{ 'advanced.form.requiredMessage' | translate }}</small>
                    }

                    <div class="table-form__row table-form__row--stacked">
                      <span class="table-form__label">{{ 'advanced.form.imagesLabel' | translate }}</span>
                      <div class="table-form__value">
                        <div class="array-field">
                          @if (imagesOf(offerListings, i).length < 3) {
                            <app-file-dropzone accept="image/*" [multiple]="true"
                              [title]="'advanced.form.imagesDropTitle' | translate"
                              [hint]="'advanced.form.imagesDropHint' | translate"
                              (filesSelected)="onImagesSelected($event, offerListings, i)" />
                          }
                          <p class="table-form__hint">{{ 'advanced.form.imagesLimitHint' | translate }}</p>
                          @if (imagesOf(offerListings, i).length) {
                            <div class="file-list">
                              @for (control of imagesOf(offerListings, i).controls; track $index; let iIndex = $index) {
                                <div class="file-list__item">
                                  <span class="file-list__icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" focusable="false">
                                      <path fill="currentColor" d="M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm1 14h12l-3.75-5-2.75 3.5-2-2.5L6 18Zm2.5-8a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
                                    </svg>
                                  </span>
                                  <span class="file-list__meta">
                                    <strong>{{ imageFileName(offerListings, i, iIndex) }}</strong>
                                    <small>{{ imageFileSize(offerListings, i, iIndex) }}</small>
                                  </span>
                                  <button type="button" class="file-list__remove" (click)="removeImage(offerListings, i, iIndex)"
                                    [attr.aria-label]="'Remove ' + imageFileName(offerListings, i, iIndex)">
                                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                      <path fill="currentColor" d="m18.3 5.71-1.41-1.41L12 9.17 7.11 4.29 5.7 5.71 10.59 10.6 5.7 15.49l1.41 1.41L12 12.01l4.89 4.89 1.41-1.41-4.89-4.89Z" />
                                    </svg>
                                  </button>
                                </div>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    </div>

                    <div class="table-form__actions">
                      <button type="button" class="array-field__remove" (click)="removeRecord(offerListings, i)">
                        {{ 'advanced.form.removeRecord' | translate }}
                      </button>
                    </div>
                  </fieldset>
                }

                <div class="table-form__actions">
                  <button type="button" class="array-field__add" (click)="addOfferListing()">
                    {{ 'advanced.form.addRecord' | translate }}
                  </button>
                </div>

                <div class="table-form__actions">
                  <button type="button" class="table-form__clear" (click)="resetOfferListings()" [disabled]="isSubmitting">
                    {{ 'advanced.form.clearForm' | translate }}
                  </button>
                  <button type="submit" class="table-form__submit" [disabled]="isSubmitting">
                    {{ isSubmitting ? ('advanced.form.submitting' | translate) : ('advanced.form.submit' | translate) }}
                  </button>
                </div>

                @if (errorMessage) {
                  <p class="form-status form-status--error">{{ errorMessage }}</p>
                }
                @if (successMessage) {
                  <p class="form-status form-status--success">{{ successMessage }}</p>
                }
              </form>
            }
          }
        </article>
      </section>
    </main>

    <app-footer [darkMode]="darkMode()" />
  </div>
</section>
